import { ProvenanceComparison } from "../components/ProvenancePanel";
import { useEffect, useMemo, useState } from "react";
import type {
  ReleaseConfiguration,
  ReleaseNotesScope,
  ReleasePoint,
  TimelineCommit,
} from "@insights/contracts";
import {
  ActionIcon,
  Badge,
  Card,
  Combobox,
  Toast,
} from "@insights/ui";
import {
  AlertTriangle,
  Check,
  ClipboardCopy,
  GitBranch,
  RefreshCw,
  Tag,
} from "lucide-react";
import { useParams } from "react-router";
import { EmptyState, Loading, PageHeader } from "../../../shared/components";
import { AppShell } from "../../../shared/layout";
import { useBuildConfiguration } from "../hooks/useBuildConfiguration";
import {
  useReleaseNotes,
  useReleaseNotesScopes,
  useReleasePoints,
} from "../hooks/useReleaseNotes";
import { useCopyReleaseNotes } from "../hooks/useCopyReleaseNotes";
import { releaseNotesCommitPresentation } from "@insights/release-notes";
import { getReleaseNotesSelection } from "../helpers/get-release-notes-selection";
import "../styles/release-notes.css";

function scopeValue(scope: ReleaseNotesScope) {
  return JSON.stringify([scope.repository, scope.profile]);
}

function pointLabel(point: ReleasePoint) {
  const prefix = point.kind === "head" ? `HEAD · ${point.branchOrTag}` : point.tag;

  return `${prefix ?? point.branchOrTag} · ${point.head.sha.slice(0, 7)}`;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function ReleaseCommitList({
  commits,
  configuration,
  removed,
  repository,
}: {
  commits: TimelineCommit[];
  configuration: ReleaseConfiguration;
  removed: boolean;
  repository: string;
}) {
  if (!commits.length) {
    return null;
  }

  return (
    <section className="release-notes-commit-section">
      <h2>{removed ? "Removed" : "Changes"}</h2>
      <ul>
        {commits.map((commit) => {
          const presentation = releaseNotesCommitPresentation(
            commit,
            removed,
            configuration,
          );

          return (
            <li key={`${removed ? "removed" : "introduced"}-${commit.sha}`}>
              <Badge tone={removed ? "danger" : "neutral"}>
                {presentation.category.emoji} {presentation.category.name}
              </Badge>
              <div>
                <a
                  href={`https://github.com/${repository}/commit/${commit.sha}`}
                  rel="noreferrer"
                  target="_blank"
                >
                  <code>{commit.sha.slice(0, 7)}</code>
                  <strong>{presentation.subject || "Untitled commit"}</strong>
                </a>
                <span>
                  {commit.author} · {formatDate(commit.timestamp)}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function ReleaseNotesPage() {
  const { workspaceId } = useParams();
  const scopes = useReleaseNotesScopes(workspaceId);
  const configuration = useBuildConfiguration(workspaceId).query;
  const [selectedScopeValue, setSelectedScopeValue] = useState<string>();
  const [selectedFromPointId, setSelectedFromPointId] = useState<string>();
  const [selectedToPointId, setSelectedToPointId] = useState<string>();
  const clipboard = useCopyReleaseNotes(
    workspaceId,
    JSON.stringify(configuration.data?.configuration),
  );

  useEffect(() => {
    if (!selectedScopeValue && scopes.data?.[0]) {
      setSelectedScopeValue(scopeValue(scopes.data[0]));
    }
  }, [scopes.data, selectedScopeValue]);

  const selectedScope = scopes.data?.find(
    (scope) => scopeValue(scope) === selectedScopeValue,
  );
  const points = useReleasePoints(workspaceId, selectedScope);
  const releasePoints = points.data ?? [];
  const { effectiveFromPointId, effectiveToPointId, fromPoints } =
    getReleaseNotesSelection(
      releasePoints,
      selectedFromPointId,
      selectedToPointId,
    );
  const notes = useReleaseNotes(
    workspaceId,
    selectedScope,
    effectiveFromPointId,
    effectiveToPointId,
  );
  const scopeOptions = useMemo(
    () =>
      (scopes.data ?? []).map((scope) => ({
        value: scopeValue(scope),
        label: `${scope.repository} · ${scope.profile}`,
        icon: GitBranch,
      })),
    [scopes.data],
  );
  const fromOptions = fromPoints.map((point) => ({
    value: point.id,
    label: pointLabel(point),
    icon: Tag,
  }));
  const toOptions = releasePoints.map((point) => ({
    value: point.id,
    label: pointLabel(point),
    icon: point.kind === "tag" ? Tag : GitBranch,
  }));

  const exportSelection = notes.data ? {
    context: "comparison" as const,
    repository: notes.data.repository,
    profile: notes.data.profile,
    from: notes.data.fromPoint.id,
    to: notes.data.toPoint.id,
  } : undefined;
  const copyStatus = clipboard.status(exportSelection);
  const copied = copyStatus === "copied";

  function selectScope(value: string) {
    setSelectedScopeValue(value);
    setSelectedFromPointId(undefined);
    setSelectedToPointId(undefined);
  }

  return (
    <AppShell>
      <PageHeader eyebrow="Delivery stream" title="Comparison" />

      {clipboard.message && (
        <Toast
          onDismiss={clipboard.dismiss}
          title={copyStatus === "ready" ? "Release notes ready" : "Release notes could not be copied"}
          tone={copyStatus === "ready" ? "default" : "danger"}
        >
          {clipboard.message}
        </Toast>
      )}

      {scopes.isLoading && <Loading />}
      {scopes.isError && (
        <EmptyState
          title="Release profiles could not be loaded"
          text="Refresh to try again."
        />
      )}
      {scopes.data?.length === 0 && (
        <EmptyState
          title="No tagged releases"
          text="Configure a release profile and run a successful tagged deployment."
        />
      )}

      {scopes.data && scopes.data.length > 0 && (
        <>
          <div className="release-notes-selectors">
            <Combobox
              label="Release profile"
              onValueChange={selectScope}
              options={scopeOptions}
              placeholder="Select a profile"
              {...(selectedScopeValue ? { value: selectedScopeValue } : {})}
            />
            <Combobox
              disabled={!fromOptions.length}
              label="From tag"
              onValueChange={setSelectedFromPointId}
              options={fromOptions}
              placeholder="From tag"
              {...(effectiveFromPointId
                ? { value: effectiveFromPointId }
                : {})}
            />
            <Combobox
              disabled={!toOptions.length}
              label="To tag or head"
              onValueChange={(value) => {
                setSelectedToPointId(value);
                setSelectedFromPointId(undefined);
              }}
              options={toOptions}
              placeholder="To tag or HEAD"
              {...(effectiveToPointId ? { value: effectiveToPointId } : {})}
            />
            <ActionIcon
              disabled={scopes.isFetching || points.isFetching || notes.isFetching}
              icon={<RefreshCw className={notes.isFetching ? "refreshing" : undefined} />}
              label="Refresh release notes"
              onClick={() => {
                void scopes.refetch();
                if (selectedScope) {
                  void points.refetch();
                }
                if (effectiveFromPointId && effectiveToPointId) {
                  void notes.refetch();
                }
              }}
            />
          </div>

          {points.isLoading && <Loading />}
          {points.isError && (
            <EmptyState
              title="Release points could not be loaded"
              text="Refresh to try again."
            />
          )}
          {!points.isLoading && points.data && !fromOptions.length && (
            <EmptyState
              title="Another tag is required"
              text="Release notes compare an older tag with a newer tag or profile HEAD."
            />
          )}
          {notes.isLoading && <Loading />}
          {notes.isError && (
            <EmptyState
              title="Release notes could not be generated"
              text="Refresh or choose another tag range."
            />
          )}

          {notes.data && configuration.data?.configuration && (
            <Card className="release-notes-preview">
              <div className="release-notes-heading">
                <div>
                  <h1>Comparison</h1>
                  <p>
                    {notes.data.repository} · {notes.data.profile} · {notes.data.environment}
                  </p>
                </div>
                <ActionIcon
                  color={copied ? "accent" : "default"}
                  icon={copied ? <Check /> : copyStatus === "generating" ? <RefreshCw /> : <ClipboardCopy />}
                  disabled={clipboard.isGenerating}
                  aria-busy={clipboard.isGenerating}
                  label={copyStatus === "generating" ? "Generating release notes…" : copyStatus === "ready" ? "Copy ready" : "Copy release notes"}
                  onClick={() => {
                    if (exportSelection) {
                      void clipboard.copy(exportSelection);
                    }
                  }}
                />
              </div>

              <dl className="release-notes-range">
                <div>
                  <dt>From</dt>
                  <dd>{pointLabel(notes.data.fromPoint)}</dd>
                </div>
                <div>
                  <dt>To</dt>
                  <dd>{pointLabel(notes.data.toPoint)}</dd>
                </div>
              </dl>

              <ProvenanceComparison workspaceId={workspaceId} fromBuildId={notes.data.fromPoint.buildId} toBuildId={notes.data.toPoint.buildId} />
              {!notes.data.historyComplete && (
                <div className="release-notes-warning" role="status">
                  <AlertTriangle aria-hidden="true" />
                  Commit ancestry is still being enriched. These notes may be incomplete.
                </div>
              )}

              <ReleaseCommitList
                commits={notes.data.introducedCommits}
                configuration={configuration.data.configuration}
                removed={false}
                repository={notes.data.repository}
              />
              <ReleaseCommitList
                commits={notes.data.removedCommits}
                configuration={configuration.data.configuration}
                removed
                repository={notes.data.repository}
              />
              {!notes.data.introducedCommits.length &&
                !notes.data.removedCommits.length && (
                  <p className="release-notes-empty">
                    No visible commit changes between the selected points.
                  </p>
                )}
            </Card>
          )}
        </>
      )}
    </AppShell>
  );
}
